// Modules
import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import classNames from 'classnames';

import { Page, PageHeader, PageContent } from 'components/page';
import { PrimaryButton } from 'components/widgets/buttons/buttons';
import { Table } from 'components/content-containers/tables';
import AgreementLink from 'components/models/agreement/agreement-link';
import CompanyLink from 'components/models/company/company-link';
import { useTaxRatesData, useUpdateTaxRates } from './hooks/useTaxRates';

// Component
function TaxRatesPage() {
  const { taxRateData, fetching } = useTaxRatesData();
  const { updateTaxRates } = useUpdateTaxRates();
  const rateData = taxRateData.rateData;
  const settings = taxRateData.settings || {};

  if (!rateData || !settings) return null;

  const staleRates = _.filter(rateData, rate => rate.isStale && rate.agreement.taxable);
  const hasStaleRates = staleRates.length > 0;

  const handleUpdateWATaxRates = () => {
    updateTaxRates();
  };

  const headerActions = (
    <div className="header-actions d-flex align-items-center">
      <b className={classNames('text-nowrap mr-3', { 'text-danger': hasStaleRates, 'text-success': !hasStaleRates })}>
        {staleRates.length} stale rates
      </b>
      <PrimaryButton
        onClick={handleUpdateWATaxRates}
        disabled={settings.updating}
        className="btn-success"
      >
        {settings.updating ? 'Updating...' : 'Update WA Tax Rates'}
      </PrimaryButton>
    </div>
  );

  let lastCompany = null;

  return (
    <Page>
      <PageHeader title="Tax Rates" actions={headerActions} />
      <PageContent
        fetching={fetching}
        emptyText="No agreements found!"
        emptyTextIcon="svg/agreement.svg"
        showEmptyText={!fetching && _.isEmpty(rateData)}
        className="tax-rates"
      >
        <div className="tax-rates-table">
          <Table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Agreement</th>
                <th>Tax Address</th>
                <th className="right-align">Tax Rate</th>
              </tr>
            </thead>
            <tbody>
              {_.map(rateData, (rate) => {
                const { agreement, company } = rate;
                const sameCompany = lastCompany === company.id;
                lastCompany = company.id;

                return (
                  <tr key={agreement.id} className={classNames({ 'bg-light': !agreement.taxable })}>
                    <td className="align-middle">
                      {!sameCompany &&
                        <CompanyLink company={company} />
                      }
                    </td>
                    <td className="align-middle">
                      <AgreementLink agreement={agreement} />
                    </td>
                    <td>
                      {agreement.taxable && rate.taxAddress &&
                        <div>
                          {rate.taxAddress.address}<br />
                          {rate.taxAddress.city}, {rate.taxAddress.state} {rate.taxAddress.zip}
                        </div>
                      }
                      {!agreement.taxable && company.billingAddress &&
                        <div>
                          {company.billingAddress.address}<br />
                          {company.billingAddress.city}, {company.billingAddress.state} {company.billingAddress.zip}
                        </div>
                      }
                    </td>
                    {!agreement.taxable ? (
                      <td className="right-align align-middle">Not taxable</td>
                    ) : (
                      <td className={classNames('right-align text-white align-middle nowrap', { 'bg-danger': rate.isStale, 'bg-success': !rate.isStale })}>
                        {rate.taxLocation &&
                          <span style={{ opacity: 0.8 }} className="font-weight-light">
                          {rate.taxLocation} -&nbsp;
                          </span>
                        }
                        {!!rate.taxRate && <span>{(rate.taxRate * 100).toFixed(2)}%</span>}
                        {!rate.taxRate && 'Not set'}<br />
                        <span style={{ opacity: 0.7, fontSize: '0.8rem' }}>
                        as of&nbsp;
                        {rate.lastChecked && moment(rate.lastChecked).format('M/D/YY h:mm a')}
                        {!rate.lastChecked && 'Never'}
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </PageContent>
    </Page>
  );
}

export default TaxRatesPage;
